import * as React from 'react';
import {observer} from 'mobx-react';
import Swiper from 'swiper';
import {CatalogEntity} from '../../../lib/Entity/CatalogEntity';
import {CatalogDisplayMode} from '../../../lib/Controller/CatalogController';
import {CatalogPageEntity} from '../../../lib/Entity/CatalogPageEntity';
import {PageGroup} from './PageGroup';

import './style.scss';

interface PagesSliderProps {
  catalog: CatalogEntity;
  displayMode: CatalogDisplayMode;
  updateActivePage: (pageIndex?: number) => void;
}

@observer
export class PagesSlider extends React.Component<PagesSliderProps, {}>{
  private containerRef: React.RefObject<HTMLDivElement> = React.createRef();
  private prevBtnRef: React.RefObject<HTMLDivElement> = React.createRef();
  private nextBtnRef: React.RefObject<HTMLDivElement> = React.createRef();
  private swiper?: Swiper;
  private panzoom?: any;
  private slidingAllowed: boolean = true;

  componentDidMount() {
    this.initSwiper();
  }

  private disableSwiper = () => {
    this.slidingAllowed = false;
    this.updateSwiperSliding();
  };

  private enableSwiper = () => {
    this.slidingAllowed = true;
    this.updateSwiperSliding();
  };

  private updateSwiperSliding = () => {
    if (this.swiper) {
      this.swiper.allowSlidePrev = this.slidingAllowed;
      this.swiper.allowSlideNext = this.slidingAllowed;
    }
  };

  private initSwiper = () => {
    const {updateActivePage} = this.props;

    if (this.swiper) {
      this.swiper.destroy(true,true);
    }

    if (this.containerRef.current) {
      this.swiper = new Swiper(this.containerRef.current, {
        direction: 'horizontal',
        loop: false,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        touchStartPreventDefault: true,
        on: {
          slideChange: () => {
            updateActivePage(this.swiper?.realIndex);
          }
        }
      });
    }

    if (this.prevBtnRef.current) {
      this.prevBtnRef.current.addEventListener('click', this.handleNavigationPrevArrowClick);
    }

    if (this.nextBtnRef.current) {
      this.nextBtnRef.current.addEventListener('click', this.handleNavigationNextArrowClick);
    }
  };

  private get pages(): CatalogPageEntity[][] {
    const {catalog, displayMode} = this.props;

    if (displayMode === CatalogDisplayMode.ONE_SITE) {
      return catalog.pagesOneSiteMode;
    } else {
      return catalog.pagesMagazineMode;
    }
  }

  private onPanZoomChange = (panzoom: any) => {
    this.panzoom = panzoom;

    if (this.panzoom.state.scale > 1) {
      this.disableSwiper();
    } else {
      this.panzoom.reset();
      this.enableSwiper();
    }
  };

  private preHandleNavigationArrowClick = (): boolean => {
    if (!this.slidingAllowed) {
      this.enableSwiper();
      if (this.panzoom) {
        this.panzoom.reset();
      }
      return true;
    }
    return false;
  };

  private handleNavigationPrevArrowClick = () => {
    const shouldChangeSlide = this.preHandleNavigationArrowClick();

    if (shouldChangeSlide) {
      if (this.swiper) {
        this.swiper.slidePrev();
      }
    }
  };

  private handleNavigationNextArrowClick = () => {
    const shouldChangeSlide = this.preHandleNavigationArrowClick();

    if (shouldChangeSlide) {
      if (this.swiper) {
        this.swiper.slideNext();
      }
    }
  };

  render() {
    return (
      <div className='pages-slider-container swiper-container' ref={this.containerRef}>
        <div className='swiper-wrapper'>
          {this.pages.map((pages, index) => {
            return (
              <PageGroup key={index} pages={pages} onPanZoomChange={this.onPanZoomChange} />
            )
          })}
        </div>
        <div className='swiper-button-prev' ref={this.prevBtnRef} />
        <div className='swiper-button-next' ref={this.nextBtnRef} />
      </div>
    );
  }
}
